import { useState } from "react";
import {
    apiService,
    type AnalyticsExportResponse,
    type AnalyticsExportRow,
    type AnalyticsFilterParams,
} from "../../services/api.service";
import { Button } from "../ui/button";
import { TablePagination } from "../ui/pagination";
import { AnalyticsFilters, type FilterDisplayState } from "./AnalyticsFilters";
import { SectionHeader } from "./AnalyticsUI";

interface Props {
    filters: AnalyticsFilterParams;
    onFiltersChange: (f: AnalyticsFilterParams) => void;
    filterDisplay: FilterDisplayState;
    onFilterDisplayChange: (d: FilterDisplayState) => void;
}

function escapeCSV(v: unknown): string {
    const s = v === null || v === undefined ? "" : String(v);
    if (s.includes(",") || s.includes('"') || s.includes("\n")) {
        return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
}

function toCSV(rows: AnalyticsExportRow[]): string {
    const headers = Object.keys(rows[0]).map(escapeCSV).join(",");
    const lines = rows.map((r) => Object.values(r).map(escapeCSV).join(","));
    return [headers, ...lines].join("\n");
}

function downloadCSV(rows: AnalyticsExportRow[]): void {
    const csv = toCSV(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    const date = new Date().toISOString().slice(0, 10);
    anchor.download = `sessions_export_${date}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(objectUrl);
}

const COLUMNS: { key: keyof AnalyticsExportRow; label: string; className?: string }[] = [
    { key: "session_id", label: "Session ID", className: "w-[120px] max-w-[120px] truncate font-mono" },
    { key: "date", label: "Date", className: "w-[90px]" },
    { key: "student_name", label: "Student", className: "w-[130px] max-w-[130px] truncate" },
    { key: "student_email", label: "Email", className: "w-[160px] max-w-[160px] truncate" },
    { key: "lesson_id", label: "Lesson ID", className: "w-[80px] text-right" },
    { key: "lesson_name", label: "Lesson", className: "w-[200px] max-w-[200px] truncate" },
    { key: "time_spent_min", label: "Time (min)", className: "w-[80px] text-right" },
    { key: "completion_pct", label: "Compl. %", className: "w-[75px] text-right" },
    { key: "lesson_status", label: "Status", className: "w-[90px]" },
    { key: "max_slide_reached", label: "Max Slide", className: "w-[75px] text-right" },
    { key: "total_slides", label: "Slides", className: "w-[55px] text-right" },
    { key: "total_attempts", label: "Attempts", className: "w-[65px] text-right" },
    { key: "correct_count", label: "Correct", className: "w-[60px] text-right" },
    { key: "incorrect_count", label: "Incorrect", className: "w-[65px] text-right" },
    { key: "accuracy_pct", label: "Acc. %", className: "w-[60px] text-right" },
    { key: "help_requests", label: "Help", className: "w-[50px] text-right" },
    { key: "scaffolding_rate_pct", label: "Scaff. %", className: "w-[65px] text-right" },
    { key: "exit_ticket_score", label: "Exit Score", className: "w-[75px] text-right" },
];

export const AnalyticsExport = ({
    filters,
    onFiltersChange,
    filterDisplay,
    onFilterDisplayChange,
}: Props) => {
    const [data, setData] = useState<AnalyticsExportResponse | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 100;

    const handleFetch = async (params: AnalyticsFilterParams = filters) => {
        try {
            setIsLoading(true);
            setError(null);
            const result = await apiService.getAnalyticsExport(params);
            setData(result);
        } catch {
            setError("Failed to load export data");
        } finally {
            setIsLoading(false);
        }
    };

    const handleFilterApply = (newFilters: AnalyticsFilterParams) => {
        onFiltersChange(newFilters);
        setPage(1);
        handleFetch(newFilters);
    };

    const totalRows = data?.rows.length ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));
    const startIdx = (page - 1) * PAGE_SIZE;
    const previewRows = data?.rows.slice(startIdx, startIdx + PAGE_SIZE) ?? [];

    return (
        <div className="min-w-0 space-y-4">
            <SectionHeader
                title="Export"
                description="Raw session-level data for every student and lesson. Apply filters then download as CSV."
            />
            <AnalyticsFilters
                filters={filters}
                onApply={handleFilterApply}
                filterDisplay={filterDisplay}
                onFilterDisplayChange={onFilterDisplayChange}
            />

            {isLoading && (
                <p className="text-sm text-muted-foreground">Loading…</p>
            )}

            {!isLoading && data && data.rows.length > 0 && (
                <div className="flex items-center gap-3">
                    <Button onClick={() => downloadCSV(data.rows)}>
                        Download CSV ({data.total} rows)
                    </Button>
                </div>
            )}

            {error && (
                <div className="flex items-center gap-3 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                    <span>{error}</span>
                    <Button variant="outline" size="sm" onClick={() => handleFetch(filters)}>
                        Retry
                    </Button>
                </div>
            )}

            {!isLoading && data && data.rows.length === 0 && (
                <p className="py-8 text-center text-sm text-muted-foreground">
                    No sessions found for the selected filters.
                </p>
            )}

            {!isLoading && data && data.rows.length > 0 && (
                <div className="overflow-x-auto rounded-md border">
                    <TablePagination
                        page={page}
                        totalPages={totalPages}
                        showingFrom={startIdx + 1}
                        showingTo={Math.min(startIdx + PAGE_SIZE, totalRows)}
                        total={totalRows}
                        onPageChange={setPage}
                        className="border-b border-t-0"
                    />
                    <div className="w-full overflow-x-auto">
                        <table className="w-max text-xs">
                            <thead>
                                <tr className="border-b bg-muted/50">
                                    {COLUMNS.map((col) => (
                                        <th
                                            key={col.key}
                                            className={`px-3 py-2 text-left font-medium text-muted-foreground whitespace-nowrap ${col.className ?? ""}`}
                                        >
                                            {col.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {previewRows.map((row, i) => (
                                    <tr key={startIdx + i} className="border-b last:border-0 hover:bg-muted/30">
                                        {COLUMNS.map((col) => {
                                            const val = row[col.key];
                                            const display = val === null || val === undefined ? "" : String(val);
                                            return (
                                                <td
                                                    key={col.key}
                                                    className={`px-3 py-1.5 overflow-hidden ${col.className ?? ""}`}
                                                    title={display}
                                                >
                                                    {display}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <TablePagination
                        page={page}
                        totalPages={totalPages}
                        showingFrom={startIdx + 1}
                        showingTo={Math.min(startIdx + PAGE_SIZE, totalRows)}
                        total={totalRows}
                        onPageChange={setPage}
                    />
                </div>
            )}
        </div>
    );
};
